import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CustomCheckbox,
  CustomCollapseCard,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomMaskInput,
  CustomSelect,
  CustomTextInput,
  Option,
  Text,
  useText,
} from '@src/components';

import { Button } from 'antd';

import { cardTypeIcon } from '@src/utils/utils';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { formCreditCardRegex } from '@src/utils/formRule';
import { monthsList, creditCardYears } from '@src/store/enums';

import { prevStep } from '@src/store/slice/autoUploadInstructionsSlice';
import { getBankOfBinNumber, setPayWithLinkinEmail } from '@src/store/slice/mobilExpressSlice';

import iconBack from '@src/assets/icons/icon-back.svg';

const NewCreditCards = ({ form, handleRegisteredCreditCard, counter, setCounter = () => {} }) => {
  const isMobil = useMediaQuery({ query: '(max-width: 1199.98px)' });

  const dispatch = useDispatch();

  const { storedCard, payWithLinkinEmail } = useSelector((state) => state?.mobilExpress);

  const [afterMount, setAfterMount] = useState(false);
  const [isStoreCard, setIsStoreCard] = useState(false);
  const [cardTypeImage, setCardTypeImage] = useState(false);

  const nameOnCard = useText('nameOnCard');
  const pleaseEnterNameOnCard = useText('pleaseEnterNameOnCard');
  const monthText = useText('month2');
  const yearText = useText('year2');
  useEffect(() => {
    setCounter((count) => count + 1);
    return () => {
      setCounter((count) => count + 1);
    };
  }, []);

  const isValid = useMemo(() => {
    const values = form?.getFieldsValue();

    const cardNumber = values?.cardNumber?.replaceAll('_', '').replaceAll('-', '');
    const cardNumberLength = cardNumber?.length;
    const cardNumberIsValid = cardNumberLength === 16;

    const cardHolderName = values?.cardHolderName;
    const cardHolderNameIsValid = cardHolderName?.length > 0;

    const lastMonth = values?.lastMonth;
    const lastMonthIsValid = lastMonth > 0;

    const lastYear = values?.lastYear;
    const lastYearIsValid = lastYear?.length > 0;

    const cvv = values?.cvv?.replaceAll('_', '');
    const cvvIsValid = cvv?.length > 2;
    return (
      cardNumberIsValid &&
      cardHolderNameIsValid &&
      lastMonthIsValid &&
      lastYearIsValid &&
      cvvIsValid
    );
  });

  useEffect(() => {
    dispatch(setPayWithLinkinEmail(false));
  }, []);

  useEffect(() => {
    if (payWithLinkinEmail && isStoreCard) {
      form.setFieldsValue({
        cardSaveChecked: false,
      });
    }
  }, [payWithLinkinEmail]);

  function handleSaveCardChecked(e) {
    setIsStoreCard(e.target.checked);
    dispatch(setPayWithLinkinEmail(false));
  }

  const handleOrderBack = useCallback(async () => {
    await dispatch(prevStep());
  }, [dispatch]);

  const cardNumberChange = useCallback(
    async (value) => {
      const cardNumber = value.replaceAll('_', '').replaceAll('-', '');
      if (cardNumber.length === 6) {
        const body = {
          bin: cardNumber,
        };
        const action = await dispatch(getBankOfBinNumber(body));
        if (getBankOfBinNumber.fulfilled.match(action)) {
          setCardTypeImage(cardTypeIcon(action.payload.data?.cardType));
        }
      }
    },
    [dispatch],
  );

  const isVerifiedPayment = useCallback(async () => {
    const values = await form.getFieldsValue();
    const { cardSaveChecked } = values;

    if (cardSaveChecked === false) {
      window.modalAction('payWithNewCardAutoUpload', true);
      return null;
    }

    if (afterMount && !isValid && cardSaveChecked === true) {
      window.modalAction('payWithNewCard', true);
      return null;
    }

    window.handlePayment();
  }, [afterMount, isValid]);

  return (
    <>
      <div className="saved-credit-cards-card new-credit-card-page">
        <ParentElement
          storedCard={storedCard}
          handleRegisteredCreditCard={handleRegisteredCreditCard}
        >
          <div className="new-credit-card-content">
            <CustomForm
              name="creditCardForm"
              className="credit-card-form"
              initialValues={{
                cardSaveChecked: false,
              }}
              autoComplete="off"
              layout={'vertical'}
              form={form}
            >
              <div className="row">
                <div className="col-lg-6">
                  <CustomFormItem
                    label={<Text t="nameOnCard" />}
                    name="cardHolderName"
                    rules={[{ required: true, message: pleaseEnterNameOnCard }]}
                  >
                    <CustomTextInput
                      id="ticketRestaurant-balanceUpload-payment-newCreditCards-cardHolderName-textInput"
                      onInput={() => setCounter((count) => count + 1)}
                      placeholder={nameOnCard}
                    />
                  </CustomFormItem>
                </div>
                <div className="col-lg-6">
                  <CustomFormItem
                    label={<Text t="cardNumber2" />}
                    name="cardNumber"
                    className="card-number-form-item"
                    rules={[
                      { required: true, message: <Text t="pleaseEnterCardNumber" /> },
                      {
                        validator: formCreditCardRegex,
                        message: <Text t="pleaseEnterAValidCardNumber" />,
                      },
                      {
                        validator: async (field, value) => {
                          setCounter((count) => count + 1);
                          return Promise.resolve();
                        },
                        message: '',
                      },
                    ]}
                  >
                    <CardInput
                      id="ticketRestaurant-balanceUpload-payment-newCreditCards-cardNumber-cardInput"
                      onInput={() => setCounter((count) => count + 1)}
                      cardTypeImage={cardTypeImage}
                      cardNumberChange={cardNumberChange}
                    />
                  </CustomFormItem>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-6">
                  <CustomFormItem
                    name={'expireMonthYear'}
                    className="expire-form-item"
                    label={
                      <span>
                        <Text t="expirationDate" />
                        <span className="form-required-mark">*</span>
                      </span>
                    }
                  >
                    <div className="row">
                      <div
                        className="ant-col-xs-12"
                        style={isMobil ? { padding: '0 10px 0 0' } : null}
                      >
                        <CustomFormItem
                          label={false}
                          name="lastMonth"
                          rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                        >
                          <CustomSelect
                            id="ticketRestaurant-balanceUpload-payment-newCreditCards-lastMonth-select"
                            onChange={() => setCounter((count) => count + 1)}
                            placeholder={monthText}
                          >
                            {monthsList.map((month) => (
                              <Option
                                id={`ticketRestaurant-balanceUpload-payment-newCreditCards-lastMonth-${month?.value}-select`}
                                key={month.value}
                                value={month.value}
                              >
                                {month.value}
                              </Option>
                            ))}
                          </CustomSelect>
                        </CustomFormItem>
                      </div>

                      <div
                        className="ant-col-xs-12"
                        style={isMobil ? { padding: '0 10px 0 0' } : null}
                      >
                        <CustomFormItem
                          label={false}
                          name="lastYear"
                          rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                        >
                          <CustomSelect
                            id="ticketRestaurant-balanceUpload-payment-newCreditCards-lastYear-select"
                            onChange={() => setCounter((count) => count + 1)}
                            placeholder={yearText}
                          >
                            {creditCardYears.map((year) => (
                              <Option
                                id={`ticketRestaurant-balanceUpload-payment-newCreditCards-lastYear-${year.value}-select`}
                                key={year.value}
                                value={year.value}
                              >
                                {year.value}
                              </Option>
                            ))}
                          </CustomSelect>
                        </CustomFormItem>
                      </div>
                    </div>
                  </CustomFormItem>
                </div>
                <div className="col-lg-6">
                  <CustomFormItem
                    label={
                      <span>
                        <Text t="securityCode" />
                        <span className="form-required-mark">*</span>{' '}
                      </span>
                    }
                    name="cvv"
                    className="cvv-form-item"
                    rules={[
                      {
                        validator: async (field, val) => {
                          const value = val?.replace('_', '').split('');
                          try {
                            setCounter((count) => count + 1);
                            if (value.length === 3) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error());
                          } catch (e) {
                            return Promise.reject(new Error());
                          }
                        },
                        message: <Text t="pleaseEnterTheSecurityCodeOnTheCard" />,
                      },
                    ]}
                  >
                    <CustomMaskInput
                      id="ticketRestaurant-balanceUpload-payment-newCreditCards-cvv-maskInput"
                      mask={'999'}
                    >
                      <CustomInput
                        id="ticketRestaurant-balanceUpload-payment-newCreditCards-cvv-input"
                        placeholder="CVC / CVV"
                      />
                    </CustomMaskInput>
                  </CustomFormItem>
                </div>
              </div>

              <div className="info-text-contain">
                <div className="col-md-12">
                  <div
                    style={{
                      fontFamily: 'UbuntuRegular, sans-serif',
                      fontSize: '14px',
                      textAlign: 'left',
                      color: '#1d2228',
                    }}
                  >
                    <Text t="youCanSaveYourCardInformationSoThat" />
                  </div>
                </div>
                <br />
                <div className="col-md-12">
                  <CustomFormItem label={false} name="cardSaveChecked" valuePropName="checked">
                    <CustomCheckbox
                      id="ticketRestaurant-balanceUpload-payment-newCreditCards-cardSaveChecked-checkbox"
                      value={isStoreCard}
                      onChange={handleSaveCardChecked}
                    >
                      <span className="info-text-title">
                        <Text t="iWantToSecurelySaveMyCardInformation" />
                      </span>
                    </CustomCheckbox>
                  </CustomFormItem>
                </div>
              </div>
            </CustomForm>
          </div>
        </ParentElement>
      </div>

      <div className={'pay-buttons-where-under-page'}>
        <div className="step-btn-container">
          <Button
            id="balanceUpload-payment-newCreditCards-back-btn"
            className="back-btn"
            onClick={handleOrderBack}
          >
            <CustomImage src={iconBack} />
            <Text t="turnBack" />
          </Button>
        </div>

        <Button
          id="balanceUpload-payment-newCreditCards-payment-btn"
          // disabled={afterMount ? false : !isValid}
          onClick={isVerifiedPayment}
          className="payment-btn"
        >
          <Text t="continue" />
        </Button>
      </div>
    </>
  );
};

export default NewCreditCards;

const CardInput = ({ cardTypeImage, cardNumberChange, onChange, ...props }) => {
  const enterYour16DigitCardNumber = useText('enterYour16DigitCardNumber');
  const change = (e) => {
    cardNumberChange?.(e.target.value);
    onChange?.(e);
  };

  return (
    <CustomMaskInput
      id="companyInformation-creditCards-newCreditCard-creditCardForm-card-maskInput"
      {...props}
      mask={'9999-9999-9999-9999'}
      onChange={change}
    >
      <CustomInput
        id="companyInformation-creditCards-newCreditCard-creditCardForm-card-input"
        placeholder={enterYour16DigitCardNumber}
        suffix={cardTypeImage ? <CustomImage src={cardTypeImage} /> : <span />}
      />
    </CustomMaskInput>
  );
};

const ParentElement = ({ children, storedCard, handleRegisteredCreditCard }) => {
  return (
    <CustomCollapseCard
      headerRightInfo={
        storedCard.length > 0 && (
          <div className="header-btn" onClick={handleRegisteredCreditCard}>
            <Text t="iWantToPayWithMyRegisteredCredit" />
          </div>
        )
      }
      cardTitle={
        <div className="title">
          <Text t="savedCreditCardsTitle" />
        </div>
      }
    >
      <div className="custom-card-body">{children}</div>
    </CustomCollapseCard>
  );
};
